import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormGroup, UntypedFormBuilder, Validators, NgForm, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { SubSink } from 'subsink';
import { Router, ActivatedRoute } from '@angular/router';
import { LoggerService } from 'src/app/core/services/logger.service';
import { ForgotPasswordModel } from '../shared/forgot-password-model';
import { Response } from 'src/app/shared/services/response';
import { ROUTE_SIGNIN } from 'src/app/authentication/shared/routes';
import { ForgotPasswordService } from '../forgot-password.service';
import { ROUTE_NOTIFICATION_SENT } from '../shared/routes';
import { INVALID_TOKEN } from '../shared/guards/messages';
import { AuthenticationService } from 'src/app/authentication/shared/authentication.service';
import { ToastService } from 'src/app/shared/toast/toast.service';
import {
  EMAIL_ALREADY_VERIFIED_CODE,
  RESEND_VERIFICATION_COOLDOWN_MS,
  RESEND_VERIFICATION_ERROR_MESSAGE,
  USER_NOT_CONFIRMED_CODE,
  VERIFICATION_EMAIL_SENT_CODE,
  VERIFICATION_EMAIL_SENT_MESSAGE,
  VERIFICATION_REQUIRED_MESSAGE
} from 'src/app/authentication/shared/email-verification';

const RequiredFieldMessage = 'You must enter a value';
const InvalidEmailAddress = 'Invalid email address';

@Component({
    selector: 'app-forgot-password',
    templateUrl: './forgot-password.component.html',
    styleUrls: ['./forgot-password.component.scss'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormsModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatIconModule, RouterModule]
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  loading: boolean;
  message: string;
  errorMessage: string;
  verificationRequired = false;
  resendVerificationLoading = false;
  resendVerificationCooldown = false;

  private subs = new SubSink();
  private resendCooldownTimer: ReturnType<typeof setTimeout>;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly forgotPasswordService: ForgotPasswordService,
    private readonly logger: LoggerService,
    private readonly authenticationService: AuthenticationService,
    private readonly toastService: ToastService
  ) {
    this.loading = false;
    this.message = `Enter your email address to receive a reset link.`;

    this.errorMessage = undefined;
    if (INVALID_TOKEN === this.activatedRoute.snapshot.queryParams.message) {
      this.errorMessage = 'There was an error with changing your password. Please try again or contact us for help.';
    }

    this.form = this.createForm();
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    clearTimeout(this.resendCooldownTimer);
  }

  onSubmit(forgotPasswordForm: NgForm) {
    if (!this.form.valid) {
      return;
    }

    this.loading = true;
    const model = this.prepareSaveModel();

    this.subs.add(
      this.forgotPasswordService.sendForgotPasswordNotice(model).subscribe(
        (result: ForgotPasswordModel) => {
          this.errorMessage = undefined;
          this.form.reset();
          forgotPasswordForm.resetForm();
          this.loading = false;
          this.navigateToNotificationSent();
        },
        (error: Response) => {
          this.loading = false;

          if (error?.code === USER_NOT_CONFIRMED_CODE) {
            this.verificationRequired = true;
            this.errorMessage = VERIFICATION_REQUIRED_MESSAGE;
          } else {
            this.errorMessage = error.message;
            this.navigateToNotificationSent();
          }
        }
      )
    );
  }

  resendVerificationEmail(): void {
    if (this.resendVerificationLoading || this.resendVerificationCooldown) {
      return;
    }

    const username = (this.email.value as string).trim();
    this.resendVerificationLoading = true;
    this.startResendCooldown();

    this.subs.add(
      this.authenticationService.resendVerificationEmail(username).subscribe({
        next: response => {
          this.resendVerificationLoading = false;

          if (response.code === VERIFICATION_EMAIL_SENT_CODE) {
            this.toastService.showSuccess(VERIFICATION_EMAIL_SENT_MESSAGE);
          } else if (response.code === EMAIL_ALREADY_VERIFIED_CODE) {
            this.toastService.showInfo('Your email is already verified. Please sign in.');
            this.onNavigateToSignIn();
          } else {
            this.toastService.showError(RESEND_VERIFICATION_ERROR_MESSAGE);
          }
        },
        error: () => {
          this.resendVerificationLoading = false;
          this.toastService.showError(RESEND_VERIFICATION_ERROR_MESSAGE);
        }
      })
    );
  }

  onNavigateToSignIn(): void {
    this.router.navigate([`/${ROUTE_SIGNIN}`]);
  }

  get email() {
    return this.form.get('email');
  }

  getEmailErrorMessage() {
    const errors = this.email.errors;
    return errors.required ? RequiredFieldMessage : errors.pattern ? InvalidEmailAddress : '';
  }

  private createForm(): UntypedFormGroup {
    const form = this.fb.group({
      email: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$')]]
    });

    return form;
  }

  private prepareSaveModel(): ForgotPasswordModel {
    const formModel = this.form.value;

    const model = {
      username: formModel.email as string
    } as ForgotPasswordModel;

    return model;
  }

  navigateToNotificationSent(): void {
    this.router.navigate([`/${ROUTE_NOTIFICATION_SENT}`]);
  }

  private startResendCooldown(): void {
    this.resendVerificationCooldown = true;
    clearTimeout(this.resendCooldownTimer);
    this.resendCooldownTimer = setTimeout(() => {
      this.resendVerificationCooldown = false;
    }, RESEND_VERIFICATION_COOLDOWN_MS);
  }
}
